import type {
  TurnResponse,
  TurnState,
  WbAgentClient,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {TERMINAL_STATES} from '@wandb/weave/lib/WbAgent/WbAgentClient';

import {getNextTurnPermissions} from './devPermissions';
import {extractErrorMessage} from './errors';
import type {RuntimeEvent} from './events';
import type {AgentRuntime, ResumeTurnParams, RunTurnParams} from './runtime';
import {
  extractTurnId,
  terminalEventForTurnState,
  TurnEventExtractor,
} from './turn';

export type GetParentNodeId = (nodeId: string) => string | null;

const POLL_INITIAL_MS = 1000;
const POLL_SETTLED_MS = 3000;
const POLL_MAX_MS = 5000;
const POLL_FACTOR = 1.5;

/**
 * Poll-based runtime backed by the wb_agent service.
 *
 * Creates turns via `POST /turns`, `polls GET /turns/{id}` with adaptive
 * backoff, and synthesizes {@link RuntimeEvent}s from turn snapshots.
 */
export class WBAgentRuntime implements AgentRuntime {
  private controllers = new Map<string, AbortController>();

  constructor(
    private readonly client: WbAgentClient,
    private readonly getParentNodeId: GetParentNodeId
  ) {}

  cancel(threadId: string): void {
    const controller = this.controllers.get(threadId);
    if (controller) {
      controller.abort();
      this.controllers.delete(threadId);
    }
  }

  async *resumeTurn({
    turnId,
    threadId,
    signal: externalSignal,
    initialSnapshot,
  }: ResumeTurnParams): AsyncGenerator<RuntimeEvent, void, undefined> {
    const {signal, finish} = this.startOperation(threadId, externalSignal);

    const runId = crypto.randomUUID();
    yield {type: 'run-started', runId, threadId};

    const extractor = new TurnEventExtractor(turnId);
    extractor.extract(initialSnapshot);

    try {
      if (isTerminal(initialSnapshot.state)) {
        const event = terminalEventForTurnState(
          initialSnapshot.state,
          runId,
          initialSnapshot.final_output
        );
        if (event) {
          yield event;
        }

        return;
      }

      yield* this.pollTurn(turnId, runId, signal, extractor);

      if (signal.aborted) {
        await this.cancelTurn(turnId);
        yield {type: 'run-cancelled', runId};
      }
    } catch (err) {
      if (signal.aborted) {
        await this.cancelTurn(turnId);
        yield {type: 'run-cancelled', runId};
      } else {
        yield {
          type: 'run-error',
          message: extractErrorMessage(err),
          runId,
        };
      }
    } finally {
      finish();
    }
  }

  async *runTurn({
    threadId,
    userMessage,
    parentId,
    signal: externalSignal,
  }: RunTurnParams): AsyncGenerator<RuntimeEvent, void, undefined> {
    const {signal, finish} = this.startOperation(threadId, externalSignal);

    // NOTE: Client-only; wb_agent doesn't have run IDs. This helps uniquely
    // identify conversational turns within the frontend chat state.
    const runId = crypto.randomUUID();
    yield {type: 'run-started', runId, threadId};

    let turnId: string | null = null;
    try {
      const result = await this.createTurn(
        userMessage,
        parentId ?? null,
        runId,
        signal
      );
      if (!result.ok) {
        yield result.event;
        return;
      }

      turnId = result.turnId;
      yield {type: 'turn-created', rootTurnId: result.rootTurnId};

      if (signal.aborted) {
        await this.cancelTurn(turnId);
        yield {type: 'run-cancelled', runId};
        return;
      }

      yield* this.pollTurn(turnId, runId, signal);

      // `pollTurn` breaks (without yielding) on abort; handle it here.
      if (signal.aborted) {
        await this.cancelTurn(turnId);
        yield {type: 'run-cancelled', runId};
      }
    } catch (err) {
      if (signal.aborted) {
        if (turnId) {
          await this.cancelTurn(turnId);
        }

        yield {type: 'run-cancelled', runId};
      } else {
        yield {
          type: 'run-error',
          message: extractErrorMessage(err),
          runId,
        };
      }
    } finally {
      finish();
    }
  }

  /**
   * startOperation cancels any previous in-flight operation for the thread and
   * creates a new internal `AbortController`. If the external signal aborts,
   * the internal controller is also aborted.
   *
   * Returns the merged signal and a scoped `finish` callback. The callback
   * only removes the controller if it hasn't been replaced by a newer
   * operation, preventing a stale finally-block from deleting a successor's
   * controller.
   */
  private startOperation(
    threadId: string,
    externalSignal: AbortSignal
  ): {signal: AbortSignal; finish: () => void} {
    this.cancel(threadId);

    const controller = new AbortController();
    this.controllers.set(threadId, controller);

    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', () => controller.abort(), {
        once: true,
      });
    }

    return {
      signal: controller.signal,
      finish: () => {
        if (this.controllers.get(threadId) === controller) {
          this.controllers.delete(threadId);
        }
      },
    };
  }

  private async createTurn(
    userMessage: string | undefined,
    parentId: string | null,
    runId: string,
    signal: AbortSignal
  ): Promise<
    | {ok: true; turnId: string; rootTurnId: string}
    | {ok: false; event: RuntimeEvent}
  > {
    const parentTurnId = this.resolveParentTurnId(parentId);

    if (signal.aborted) {
      return {ok: false, event: {type: 'run-cancelled', runId}};
    }

    const turnResponse = await this.client.post(
      '/turns',
      {
        // entity/project only scope the root turn; child turns inherit from
        // the root, so omit them when a parent_turn_id is set.
        ...(parentTurnId == null
          ? {entity: this.client.entity, project: this.client.project}
          : {}),
        parent_turn_id: parentTurnId,
        user_prompt: userMessage ?? '',
        permissions: getNextTurnPermissions(),
      },
      undefined,
      signal
    );

    return {
      ok: true,
      rootTurnId: turnResponse.root_turn_id,
      turnId: turnResponse.id,
    };
  }

  /**
   * Polls `GET /turns/{id}` with adaptive backoff until a terminal state is
   * reached. Yields content events and terminal events. On abort, returns
   * silently (caller handles cancellation).
   */
  private async *pollTurn(
    turnId: string,
    runId: string,
    signal: AbortSignal,
    extractor?: TurnEventExtractor
  ): AsyncGenerator<RuntimeEvent, void, undefined> {
    extractor ??= new TurnEventExtractor(turnId, {
      // NOTE: User messages are optimistically added to thread state, so drop
      // them for in-progress turns.
      skipUserMessages: true,
    });

    let hasReceivedData = false;
    let interval = POLL_INITIAL_MS;
    let lastSnapshotKey = '';

    while (true) {
      if (signal.aborted) {
        break;
      }

      await sleep(interval, signal);
      if (signal.aborted) {
        break;
      }

      const turn = await this.client.get<TurnResponse>(
        `/turns/${turnId}`,
        undefined,
        signal
      );

      const snapshotKey = `${turn.state}:${(turn.messages ?? []).length}:${(turn.tool_calls ?? []).length}`;
      if (snapshotKey !== lastSnapshotKey) {
        lastSnapshotKey = snapshotKey;

        if (
          (turn.messages?.length ?? 0) > 0 ||
          (turn.tool_calls?.length ?? 0) > 0
        ) {
          hasReceivedData = true;
        }

        interval = hasReceivedData ? POLL_SETTLED_MS : POLL_INITIAL_MS;
      } else {
        interval = Math.max(
          Math.min(interval * POLL_FACTOR, POLL_MAX_MS),
          hasReceivedData ? POLL_SETTLED_MS : POLL_INITIAL_MS
        );
      }

      const events = extractor.extract(turn);
      for (const event of events) {
        yield event;
      }

      const terminalEvent = terminalEventForTurnState(
        turn.state,
        runId,
        turn.final_output
      );
      if (terminalEvent) {
        yield terminalEvent;
        return;
      }
    }
  }

  /**
   * resolveParentTurnId finds the WB agent turn ID that should be the parent of
   * a new turn anchored at `messageId`. Walks up via `getParentNodeId` until it
   * finds a node whose ID encodes a turn ID (format `${turnId}:<role>`).
   * Returns `null` if no parent turn can be found for the message.
   *
   * NOTE: This function exists because optimistic user message nodes must use
   * internal IDs, not turn-encoded IDs; a turn doesn't exist for the user
   * message until the backend creates it and returns it.
   */
  private resolveParentTurnId(messageId: string | null): string | null {
    const visited = new Set<string>();

    let current = messageId;
    while (current && !visited.has(current)) {
      const turnId = extractTurnId(current);
      if (turnId) {
        return turnId;
      }

      visited.add(current);
      current = this.getParentNodeId(current);
    }

    return null;
  }

  private async cancelTurn(turnId: string): Promise<void> {
    try {
      await this.client.post<TurnResponse>(
        `/turns/${turnId}/cancel`,
        undefined
      );
    } catch {
      // Best-effort cancel; there's nothing to do here.
    }
  }
}

function isTerminal(state: TurnState): boolean {
  return TERMINAL_STATES.includes(state);
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };

    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal.addEventListener('abort', onAbort, {once: true});
  });
}
